import * as React from 'react';
import PropTypes from 'prop-types';
import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

function NumberSpinner({ id: idProp, label, error, size = 'medium', suffix, ...other }) {
  let id = React.useId();
  if (idProp) {
    id = idProp;
  }

  const btnSize = size === 'small' ? 28 : 32;

  return (
    <BaseNumberField.Root
      {...other}
      render={(props, state) => (
        <FormControl
          size={size}
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={error}
          variant="outlined"
          sx={{ width: 'auto' }}
        >
          {props.children}
        </FormControl>
      )}
    >
      {label && (
        <Typography
          component="label"
          htmlFor={id}
          variant="body2"
          sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}
        >
          {label}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <BaseNumberField.Decrement
          render={
            <IconButton
              aria-label="Decrease"
              size="small"
              sx={{
                width: btnSize, height: btnSize,
                borderRadius: '8px',
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.selected' },
                '&.Mui-disabled': { opacity: 0.4 },
              }}
            />
          }
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </BaseNumberField.Decrement>

        <BaseNumberField.Input
          id={id}
          render={
            <Box
              component="input"
              sx={{
                width: 28,
                textAlign: 'center',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontWeight: 500,
                fontSize: size === 'small' ? '0.85rem' : '0.95rem',
                color: 'text.primary',
                fontFamily: 'inherit',
                p: 0,
              }}
            />
          }
        />

        <BaseNumberField.Increment
          render={
            <IconButton
              aria-label="Increase"
              size="small"
              sx={{
                width: btnSize, height: btnSize,
                borderRadius: '8px',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { opacity: 0.4 },
              }}
            />
          }
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </BaseNumberField.Increment>

        {suffix && (
          <Typography variant="body2" color="text.secondary">{suffix}</Typography>
        )}
      </Box>
    </BaseNumberField.Root>
  );
}

NumberSpinner.propTypes = {
  error: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.node,
  min: PropTypes.number,
  size: PropTypes.oneOf(['medium', 'small']),
  suffix: PropTypes.string,
};

export default NumberSpinner;
